import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";

import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FEES, FIRM, PENINSULA_ADDRESS, peninsulaBusinessSchema, MICHAEL_PHONE, MICHAEL_PHONE_HREF } from "@/lib/firm";
import { MORETON_BAY_LOCATIONS } from "@/lib/moreton-bay";
import { faqSchema } from "@/lib/firm";

const FAQS = [
  { q: "Where is your Moreton Bay office?", a: `At ${PENINSULA_ADDRESS}, seen by appointment. Many matters also run fully online.` },
  { q: "What do you charge for conveyancing in Redcliffe and Moreton Bay?", a: `Fixed fees: sales ${FEES.sale} and purchases ${FEES.purchase}, both including GST. Searches and government fees are quoted in writing before you commit.` },
  { q: "Do you do wills and probate on the peninsula?", a: "Yes. We prepare wills and enduring powers of attorney and help executors with probate and estate administration, with appointments at Scarborough." },
  { q: "Who will handle my matter?", a: "A solicitor, not a call centre. Legal Practice Director Michael Klein lives in Redcliffe and looks after our Moreton Bay clients." },
];

const description = `Conveyancing, wills, estates and elder law across Moreton Bay from our Scarborough office — Redcliffe, Woody Point, Margate, Clontarf, North Lakes, Strathpine, Kallangur, Caboolture and Woodford. Fixed fees: sales ${FEES.sale}, purchases ${FEES.purchase} incl. GST.`;

const collectionSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Moreton Bay conveyancing, wills and estates",
  description,
  isPartOf: { "@type": "WebSite", name: FIRM.name },
  hasPart: MORETON_BAY_LOCATIONS.map((l) => ({
    "@type": "WebPage",
    name: l.title,
    url: `https://coastsidelaw.com.au/moreton-bay/${l.slug}`,
  })),
};

export const Route = createFileRoute("/moreton-bay/")({
  head: () => ({
    meta: [
      { title: fitTitle(`Moreton Bay Conveyancing & Wills Lawyers | ${FIRM.name}`) },
      { name: "description", content: fitDescription(description) },
      { property: "og:title", content: fitTitle(`Moreton Bay conveyancing and estate lawyers | ${FIRM.name}`) },
      { property: "og:description", content: fitDescription(description) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/moreton-bay" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/moreton-bay" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(collectionSchema) },
      { type: "application/ld+json", children: JSON.stringify(peninsulaBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: MoretonBayIndex,
});

function MoretonBayIndex() {
  return (
    <>
      <PageHero
        eyebrow="Moreton Bay region"
        title="Conveyancing, wills and estates across Moreton Bay"
        lead="Our peninsula office is at 15 Fortune Street, Scarborough. Legal Practice Director Michael Klein lives in Redcliffe and spent many years as managing partner of GKS Law, and we act for clients from the peninsula to the hinterland on the same fixed fees."
      />

      <section className="mx-auto max-w-5xl px-5 py-16">
        <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
          You can meet us at our{" "}
          <Link to="/scarborough" className="font-semibold text-primary underline">
            Scarborough office
          </Link>
          , {PENINSULA_ADDRESS}, by appointment, or let the whole matter run
          electronically — Queensland conveyancing settles online through PEXA. Pick your suburb
          below for the local issues we watch for, or call{" "}
          <a href={MICHAEL_PHONE_HREF} className="font-semibold text-primary underline">
            {MICHAEL_PHONE}
          </a>{" "}
          for a fixed-fee quote.
        </p>

        <div className="mt-8 max-w-2xl rounded-sm border-2 border-accent bg-accent/15 p-5">
          <p className="font-serif text-xl text-foreground">Buying or selling in Redcliffe?</p>
          <p className="mt-2 text-sm leading-relaxed text-foreground/80">
            Our Redcliffe conveyancing page covers peninsula units, body corporate searches and
            waterfront homes, with fixed fees and a free contract review before you sign.
          </p>
          <Link
            to="/conveyancing/$suburb"
            params={{ suburb: "redcliffe" }}
            className="mt-3 inline-block text-sm font-semibold text-primary underline"
          >
            Conveyancing Redcliffe →
          </Link>
        </div>

        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MORETON_BAY_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/moreton-bay/$suburb"
                params={{ suburb: l.slug }}
                className="group flex h-full flex-col rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
              >
                <span className="font-serif text-xl text-foreground group-hover:text-primary">
                  {l.name}
                </span>
                <span className="mt-2 text-sm leading-relaxed text-muted-foreground">{l.title}</span>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Read more <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-12 flex flex-wrap items-center gap-3 rounded-sm border border-border bg-secondary/50 p-6">
          <Phone className="size-5 text-primary" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">
            Selling or buying in Moreton Bay? Sales are {FEES.sale} inclusive of GST and purchases{" "}
            {FEES.purchase} inclusive of GST.{" "}
            <Link to="/fees" className="font-semibold text-primary underline">
              See the full fee breakdown
            </Link>
            .
          </p>
        </div>
        <div className="mt-14">
          <h2 className="font-serif text-2xl text-foreground">Moreton Bay: common questions</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
